import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins, TrendingUp, TrendingDown, Loader2, Search } from 'lucide-react';
import { useCauldronTokenList } from '../hooks/useCauldronToken';
import { formatBch, formatPricePerToken, type TokenSortField, type CauldronToken } from '../lib/cauldron';

const SORTS: { key: TokenSortField; label: string }[] = [
  { key: 'tvl', label: 'Top liquidity' },
  { key: 'volume', label: 'Most traded' },
  { key: 'name', label: 'Name' },
];

export default function TokensPage() {
  const [sort, setSort] = useState<TokenSortField>('tvl');
  const [query, setQuery] = useState('');
  const { tokens, loading } = useCauldronTokenList(sort);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? tokens.filter(t =>
        t.symbol.toLowerCase().includes(q) || t.name.toLowerCase().includes(q))
    : tokens;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Coins className="w-5 h-5 text-primary" /> BCH Tokens
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          CashTokens with live liquidity on Cauldron DEX.
        </p>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search tokens by name or $symbol…"
            className="w-full rounded-xl bg-muted/50 border border-border pl-9 pr-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors"
          />
        </div>

        {/* Sort tabs */}
        <div className="flex gap-1 mb-4">
          {SORTS.map(s => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSort(s.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                sort === s.key ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {loading && tokens.length === 0 && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading tokens…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="glass-card !p-8 text-center">
            <Coins className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="font-semibold text-sm">No tokens found</p>
            {q && <p className="text-xs text-muted-foreground mt-1">Nothing matches “{query}”.</p>}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="space-y-2">
            {filtered.map(token => <TokenRow key={token.tokenId} token={token} />)}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground text-center mt-6">
          Data from the Cauldron / Riften Labs indexer. Trading happens on{' '}
          <a href="https://app.cauldron.quest" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Cauldron DEX</a>.
          Not financial advice.
        </p>
      </div>
    </div>
  );
}

function TokenRow({ token }: { token: CauldronToken }) {
  const [iconFailed, setIconFailed] = useState(false);
  const up = token.change24h >= 0;

  return (
    <Link
      to={`/token/${token.tokenId}`}
      className="glass-card !p-3 flex items-center gap-3 hover:border-primary/40 hover:shadow-md transition-all no-underline"
    >
      {iconFailed || !token.iconUrl ? (
        <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
          <Coins className="w-5 h-5" />
        </div>
      ) : (
        <img
          src={token.iconUrl}
          alt={token.symbol}
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
          className="shrink-0 w-10 h-10 rounded-full bg-muted object-cover"
          onError={() => setIconFailed(true)}
        />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-bold text-sm truncate">${token.symbol}</span>
          {token.name && token.name.toLowerCase() !== token.symbol.toLowerCase() && (
            <span className="text-xs text-muted-foreground truncate">{token.name}</span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5">TVL {formatBch(token.tvlBch)}</p>
      </div>

      <div className="shrink-0 text-right">
        <p className="font-semibold text-sm">{formatPricePerToken(token.priceUsd)}</p>
        {token.change24h !== 0 && (
          <p className={`inline-flex items-center gap-0.5 text-xs font-medium ${up ? 'text-emerald-500' : 'text-red-500'}`}>
            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {up ? '+' : ''}{token.change24h.toFixed(2)}%
          </p>
        )}
      </div>
    </Link>
  );
}
